import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo } from 'react';
import { DragHandleRounded, Timeline } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { ConnectionIconsWithInfo } from 'console/header/connection-icons';
import { DeviceStateIndicator } from 'console/shared/components/indicators/device-state';
import { PowerIcon } from 'console/shared/components/indicators/power';
import { UIControlButton } from 'console/shared/components/tab/control/ui-control';
import { preventDefault, useCallbacks, usePartial } from 'shared/hooks/utils';
import { pickDevice } from 'console/redux/main-actions';
import { leaveFromDashboard } from '../../../actions';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { useDeviceState } from 'console/shared/contexts/device/state';
import { useAction, useAppSelector, usePartialAppSelector, } from 'shared/hooks/redux/hooks';
import { selectDevice } from 'entities/device/lib/selectors';
import { DefaultTabKey } from 'console/shared/content/tab/types';
import { select_ui_settings, set_ui_settings } from 'console/redux/settings-actions';
import { useDeviceSettingsButton } from 'features/device.settings.open/ui';
export const DashboardDeviceHeader = memo(function DashboardDeviceHeader(props) {
    const { is_alarm, tab_key, device_id, dashboard_sorting_mode } = props;
    const { name, device_type, is_vehicle_device, is_legacy_device, is_climatic, capabilities, } = useDeviceConfig();
    const { icon_state, online } = useDeviceState();
    const device = usePartialAppSelector(selectDevice, device_id);
    const { dashboard_settings } = useAppSelector(select_ui_settings);
    const setUISettings = useAction(set_ui_settings);
    const setDeviceTab = useCallback((tab) => {
        setUISettings({
            dashboard_settings: {
                ...dashboard_settings,
                devices: dashboard_settings?.devices?.map((device) => device.id === device_id ? { ...device, tab } : device),
            },
        });
    }, [device_id]);
    const selectDefaultTabKey = useCallback(() => setUISettings({ selected_tab_key: tab_key }), [tab_key]);
    const onSelectDevice = useCallbacks([
        useAction(usePartial(pickDevice, device_id)),
        useAction(leaveFromDashboard),
        selectDefaultTabKey,
        preventDefault,
    ]);
    const is_graphs_active = useMemo(() => tab_key === DefaultTabKey.graphs, [tab_key]);
    const onChangeTabClick = useCallback(() => {
        if (is_graphs_active) {
            if (is_vehicle_device) {
                return setDeviceTab(DefaultTabKey.vehicle_tab);
            }
            return setDeviceTab(DefaultTabKey.heat_tab);
        }
        setDeviceTab(DefaultTabKey.graphs);
    }, [setDeviceTab, is_vehicle_device, is_graphs_active]);
    const SettingsButton = useDeviceSettingsButton(device_id);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { display: "flex", alignItems: "center", overflow: "hidden", flexGrow: 1, fontSize: "1.5rem", onClick: dashboard_sorting_mode ? undefined : onSelectDevice, children: [_jsx(DeviceStateIndicator, { state: icon_state, is_alarm: is_alarm }), _jsx(Typography, { variant: "h6", textAlign: "center", textOverflow: "ellipsis", overflow: "hidden", ml: 1, whiteSpace: "nowrap", color: is_alarm ? 'error.contrastText' : 'textPrimary', children: name })] }), _jsxs(Box, { display: "flex", columnGap: 1, alignItems: "center", color: is_alarm
                    ? 'error.contrastText'
                    : !online
                        ? 'text.disabled'
                        : 'action.active', children: [_jsx(PowerIcon, { "data-tooltip": "power-icon", capabilities: capabilities, online: online, power_source: device?.power_source, voltage: device?.voltage, device_type: device_type, color: "inherit" }), _jsx(Box, { "data-tooltip": "connection-icons", children: _jsx(ConnectionIconsWithInfo, { device_id: device_id, color: "inherit" }) })] }), _jsxs(Box, { display: "flex", alignItems: "center", columnGap: 1, color: is_alarm ? 'error.contrastText' : 'action.active', children: [_jsx(IconButton, { color: is_graphs_active ? 'primary' : 'inherit', onClick: onChangeTabClick, children: _jsx(Timeline, {}) }), _jsx(UIControlButton, { color: is_alarm ? 'inherit' : undefined }), dashboard_sorting_mode && _jsx(DragHandleRounded, {}), !is_legacy_device && _jsx(SettingsButton, {})] })] }));
});
